import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Popup } from "semantic-ui-react";

import { DATE_FORMATS_FNS, NavPortal } from "components";
import { typedQueryClient } from "hooks";
import { LocationLicenseOverviewKeys } from "modules/LocationLicenseOverview/queryKeys";
import { calculateTotalCosts, getNextMonthlyInvoicingDate } from "modules/LocationLicenseOverview/utils";
import { formatDate } from "utils/dateHelperFunctions";
import { formatPrice } from "utils/formatPrice";

import type { ExactCategory, ExactStandaloneLicense } from "models";
import type { LicenseItem } from "modules/LocationLicenseOverview/queryKeys";

export type Licenses = {
  monthlyCategories: ExactCategory[];
  monthlyStandalones: ExactStandaloneLicense[];
  oneTimeCategories: ExactCategory[];
  oneTimeStandalones: ExactStandaloneLicense[];
};

type LocationLicenseNavbarProps = {
  items: LicenseItem[];
  licensesData?: Licenses;
};

export const LocationLicenseNavbar = ({ items, licensesData }: LocationLicenseNavbarProps) => {
  const { t } = useTranslation();

  const { dealer: selectedDealer } = typedQueryClient.getQueryData(LocationLicenseOverviewKeys.filters) ?? {};

  const { totalOneTimeCosts, totalMonthlyCosts } = useMemo(
    () => ({
      totalOneTimeCosts: calculateTotalCosts(items, licensesData?.oneTimeCategories, licensesData?.oneTimeStandalones),
      totalMonthlyCosts: calculateTotalCosts(items, licensesData?.monthlyCategories, licensesData?.monthlyStandalones)
    }),
    [licensesData, items]
  );

  return (
    <NavPortal>
      <div className="LocationLicenseOverview-Navbar">
        <div className="LocationLicenseOverview-Navbar-Item">
          <span>{t("v8_total_monthly", "Total monthly")}:</span>
          {`€ ${formatPrice(totalMonthlyCosts)}`}
        </div>
        <div className="LocationLicenseOverview-Navbar-Item">
          <span>{t("v8_total_once", "Total once")}:</span>
          {`€ ${formatPrice(totalOneTimeCosts)}`}
        </div>
        <div className="LocationLicenseOverview-Navbar-Item">
          <span>{t("v8_total_this_month", "Total this month")}:</span>
          {`€ ${formatPrice(totalMonthlyCosts + totalOneTimeCosts)}`}
        </div>
        <div className="LocationLicenseOverview-Navbar-Item">
          <span>{t("v8_next_monthly_bill", "Next monthly bill")}:</span>
          {formatDate(getNextMonthlyInvoicingDate(), DATE_FORMATS_FNS.dateMonthYear)}
        </div>
        {selectedDealer?.exact_account_id && selectedDealer.is_exact_invoicing_enabled && (
          <div className="LocationLicenseOverview-Navbar-Item">
            <Popup
              wide
              size="tiny"
              position="bottom right"
              offset={[0, 1]}
              trigger={
                <span className="span-ellipsis">
                  {t("v8_dealer_invoices_activated") ||
                    "Invoices on dealer level are activated. This dealers invoices can be found on dealer license overview >Invoice History"}
                </span>
              }
              content={
                <span>
                  {t("v8_dealer_invoices_activated") ||
                    "Invoices on dealer level are activated. This dealers invoices can be found on dealer license overview >Invoice History"}
                </span>
              }
            />
          </div>
        )}
      </div>
    </NavPortal>
  );
};
